import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Footer from '@/components/feature/Footer';
import PageHead from '@/components/seo/PageHead';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { overseasSchools } from '@/mocks/overseasSchools';

import SourcedImage from '@/components/feature/SourcedImage';
import { imagesFor } from '@/mocks/overseasImages';
import { detailFor } from '@/mocks/overseasDetails';
import SchoolAccessBlock from '@/components/feature/SchoolAccessBlock';
import { accessFor } from '@/mocks/overseasAccess';
const SITE = 'https://butler-academy.jp';

export default function OverseasSchoolDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const school = overseasSchools.find((s) => s.slug === slug);
  const photos = school ? imagesFor(school.slug) : [];

  if (!school) {
    return (
      <>
        <PageHead
          title="該当する学校が見つかりません"
          description="指定された海外の執事学校は見つかりませんでした。海外の執事学校一覧から各校をご確認ください。"
          path={`/overseas-schools/${slug ?? ''}`}
          noindex
          lang={lang}
        />
        <Header solid />
        <main className="pt-40 pb-24 text-center">
          <h1 className="text-2xl mb-8">該当する学校が見つかりません</h1>
          <Link to={href('/overseas-schools')} className="text-accent-600 hover:text-accent-500 transition-colors">
            海外の執事学校一覧へ戻る
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const detail = detailFor(school.slug);
  const access = accessFor(school.slug);

  const path = `/overseas-schools/${school.slug}`;
  const pageTitle = `${school.nameJa}｜海外の執事学校`;
  const pageDesc = `${school.nameJa}（${school.name}）の概要。所在地は${school.country}・${school.city}。公式サイトの記載に基づく情報です。`;

  const relatedSchools = overseasSchools
    .filter((s) => s.region === school.region && s.slug !== school.slug)
    .slice(0, 3);

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: '執事を知る', item: `${SITE}/overseas-schools` },
      { '@type': 'ListItem', position: 3, name: '海外の執事学校', item: `${SITE}/overseas-schools` },
      { '@type': 'ListItem', position: 4, name: school.nameJa, item: `${SITE}${path}` },
    ],
  };

  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: school.name,
    url: school.official,
    address: school.address,
  };

  const jsonLd: object[] = [breadcrumbLd, organizationLd];

  return (
    <>
      <PageHead
        title={pageTitle}
        description={pageDesc}
        path={path}
        lang={lang}
        jsonLd={jsonLd}
      />
      <Header solid />
      <main className="bg-background-50">
        {/* Hero */}
        <section className="hero-billboard relative w-full h-[50vh] min-h-[380px] md:h-[60vh] md:min-h-[460px] overflow-hidden">
          <img
            src="/images/generated/philosophy-hero-2026.jpg"
            alt={school.nameJa}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  {school.nameJa}
                </h1>
                <p className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">
                  {school.name}
                </p>
                <p className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed">
                  {school.country} / {school.city}
                </p>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: '執事を知る', labelEn: 'Understanding Butlers' },
            { label: '海外の執事学校', labelEn: 'Butler Schools Around the World', to: '/overseas-schools' },
            { label: school.nameJa },
          ]}
        />

        <div className="max-w-[1200px] mx-auto px-6 md:px-8 pb-24 pt-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Main column */}
            <article className="lg:col-span-8 space-y-12">
              <section>
                <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                  概要
                </h2>
                <dl className="space-y-4 text-[15px] md:text-[16px] tracking-[0.03em] leading-relaxed">
                  <div className="flex gap-4 border-b border-background-200/60 pb-3">
                    <dt className="text-foreground-400 shrink-0 w-24">国</dt>
                    <dd className="text-foreground-700">{school.country}</dd>
                  </div>
                  <div className="flex gap-4 border-b border-background-200/60 pb-3">
                    <dt className="text-foreground-400 shrink-0 w-24">都市</dt>
                    <dd className="text-foreground-700">{school.city}</dd>
                  </div>
                  <div className="flex gap-4 border-b border-background-200/60 pb-3">
                    <dt className="text-foreground-400 shrink-0 w-24">地域</dt>
                    <dd className="text-foreground-700">{school.region}</dd>
                  </div>
                  <div className="flex gap-4 pb-3">
                    <dt className="text-foreground-400 shrink-0 w-24">設立</dt>
                    <dd className="text-foreground-700">{school.founded}</dd>
                  </div>
                </dl>
              </section>

              {!detail && (
                <>
<section>
                <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                  提供している課程
                </h2>
                {school.courses.length > 0 ? (
                  <ul className="list-disc list-inside space-y-2 text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-relaxed">
                    {school.courses.map((course) => (
                      <li key={course}>{course}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-relaxed">
                    公式サイトに課程の記載はありません。
                  </p>
                )}
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                  認定・受賞
                </h2>
                {school.awards.length > 0 ? (
                  <ul className="list-disc list-inside space-y-2 text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-relaxed">
                    {school.awards.map((award) => (
                      <li key={award}>{award}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-relaxed">
                    公式サイトに認定・受賞の記載はありません。
                  </p>
                )}
              </section>

              <section>
                <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                  補足
                </h2>
                <p className="text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-loose">
                  {school.note}
                </p>
              </section>
                </>
              )}

              {access && (
                <div className="mt-14">
                  <SchoolAccessBlock access={access} schoolName={school.nameJa} />
                </div>
              )}

              {detail && (
                <>
                  {detail.sections.map((sec, i) => (
                    <div key={`detail-sec-${i}`} className="space-y-12">
                      <section>
                        <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                          {sec.heading}
                        </h2>
                        {sec.body && (
                          <p className="text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-loose">
                            {sec.body}
                          </p>
                        )}
                        {sec.bullets && sec.bullets.length > 0 && (
                          <ul className="mt-4 space-y-2 text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-relaxed">
                            {sec.bullets.map((item) => (
                              <li key={item} className="flex items-start gap-2">
                                <i className="ri-check-line text-accent-500 mt-0.5 shrink-0"></i>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {sec.table && sec.table.length > 0 && (
                          <dl className="mt-4 space-y-4 text-[15px] md:text-[16px] tracking-[0.03em] leading-relaxed">
                            {sec.table.map((row) => (
                              <div
                                key={row.label}
                                className="flex gap-4 border-b border-background-200/60 pb-3"
                              >
                                <dt className="text-foreground-400 shrink-0 w-28">{row.label}</dt>
                                <dd className="text-foreground-700">{row.value}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                        {sec.note && (
                          <p className="mt-4 text-[13px] text-foreground-500 tracking-[0.03em] leading-relaxed">
                            {sec.note}
                          </p>
                        )}
                      </section>
                      {photos.length > 0 && (i + 1) % 3 === 0 && (
                        <SourcedImage
                          image={photos[Math.floor(i / 3) % photos.length]}
                          schoolName={school.nameJa}
                          officialUrl={school.official}
                          alt={`${school.nameJa} の公式サイト掲載画像`}
                        />
                      )}
                    </div>
                  ))}
                  {detail.sources && detail.sources.length > 0 && (
                    <section>
                      <h2 className="font-heading text-2xl text-foreground-900 tracking-[0.06em] mb-5 pb-4 border-b border-background-200">
                        出典
                      </h2>
                      <ul className="space-y-2 text-[15px] md:text-[16px] text-foreground-700 tracking-[0.03em] leading-relaxed">
                        {detail.sources.map((src) => (
                          <li key={src.url}>
                            <a
                              href={src.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-accent-600 hover:text-accent-500 transition-colors inline-flex items-start gap-1"
                            >
                              <span>{src.label}</span>
                              <i className="ri-external-link-line text-xs mt-1 shrink-0"></i>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </>
              )}

              {/* Related schools in same region */}
              {relatedSchools.length > 0 && (
                <section className="pt-6 border-t border-background-200">
                  <h2 className="font-heading text-xl text-foreground-900 tracking-[0.06em] mb-6">
                    同じ地域の他校
                  </h2>
                  <ul className="space-y-3">
                    {relatedSchools.map((s) => (
                      <li key={s.slug}>
                        <Link
                          to={href(`/overseas-schools/${s.slug}`)}
                          className="flex items-center gap-2 text-accent-600 hover:text-accent-500 transition-colors text-[15px] tracking-[0.03em]"
                        >
                          <i className="ri-arrow-right-s-line text-sm"></i>
                          <span>{s.nameJa}</span>
                          <span className="text-foreground-400 text-[13px]">
                            （{s.country} / {s.city}）
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="pt-4">
                <Link
                  to={href('/overseas-schools')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  <i className="ri-arrow-left-line text-xs"></i>
                  海外の執事学校一覧へ戻る
                </Link>
              </div>
              {photos.length > 0 && (
                <section className="mt-14 border-t border-background-200 pt-10">
                  <h2 className="font-heading text-2xl mb-2">公式サイトの写真</h2>
                  <p className="text-[13px] text-foreground-500 mb-6">各画像の下に出典元を記載しています。画像の著作権は各校に帰属します。</p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-8">
                    {photos.map((img) => (
                      <SourcedImage
                        key={img.file}
                        image={img}
                        schoolName={school.nameJa}
                        officialUrl={school.official}
                        alt={school.nameJa + ' の公式サイト掲載画像'}
                      />
                    ))}
                  </div>
                </section>
              )}
            </article>

            {/* Side column */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-[120px] space-y-8">
                <section className="border border-background-200/60 bg-white">
                  <h2 className="bg-primary-900 px-5 py-3 text-accent-400 font-heading tracking-[0.06em]">
                    学校情報
                  </h2>
                  <dl className="divide-y divide-background-200/60">
                    <div className="px-5 py-4">
                      <dt className="text-xs text-foreground-400 mb-1 tracking-[0.05em]">所在地</dt>
                      <dd className="text-[14px] text-foreground-700 leading-relaxed">{school.address}</dd>
                    </div>
                    <div className="px-5 py-4">
                      <dt className="text-xs text-foreground-400 mb-1 tracking-[0.05em]">公式サイト</dt>
                      <dd>
                        <a
                          href={school.official}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[14px] text-accent-600 hover:text-accent-500 transition-colors break-all inline-flex items-start gap-1"
                        >
                          <span>{school.official}</span>
                          <i className="ri-external-link-line text-xs mt-1 shrink-0"></i>
                        </a>
                      </dd>
                    </div>
                    <div className="px-5 py-4">
                      <dt className="text-xs text-foreground-400 mb-1 tracking-[0.05em]">情報取得日</dt>
                      <dd className="text-[14px] text-foreground-700">{school.fetched}</dd>
                    </div>
                  </dl>
                  <p className="px-5 py-4 text-[12px] text-foreground-500 leading-relaxed border-t border-background-200/60">
                    本ページの記載は上記公式サイトの記載に基づきます。
                  </p>
                </section>
              </div>
            </aside>
          </div>
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={lang === 'en' ? 'Back to top' : 'ページ上部へ'}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
